import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { filesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FileText, 
  Download,
  ArrowLeft,
  File,
  FileImage,
  FileType
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

export default function CustomerDocuments() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      const response = await filesAPI.getAll();
      setFiles(response.data);
    } catch (error) {
      console.error('Failed to fetch files:', error);
      toast.error('Fehler beim Laden der Dokumente');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await filesAPI.download(file.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download fehlgeschlagen');
    }
  };

  const getFileIcon = (type) => {
    if (type === '.pdf') return FileText;
    if (['.jpg', '.jpeg', '.png'].includes(type)) return FileImage;
    if (['.doc', '.docx'].includes(type)) return FileType;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B1B30]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-4 md:py-8" data-testid="customer-documents">
      <div className="max-w-4xl mx-auto px-4 md:px-12">
        {/* Header */}
        <div className="flex items-center gap-2 md:gap-4 mb-6 md:mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="px-2 md:px-4">
              <ArrowLeft className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Zurück</span>
            </Button>
          </Link>
          <h1 className="font-barlow font-bold text-xl md:text-2xl text-[#1A1A1A]">
            Meine Dokumente
          </h1>
        </div>

        {/* Documents List */}
        <Card data-testid="documents-list">
          <CardHeader className="px-4 md:px-6">
            <CardTitle className="font-barlow text-lg md:text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#2E2A88]" />
              Dokumente ({files.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 md:px-6">
            {files.length > 0 ? (
              <div className="space-y-3">
                {files.map((file) => {
                  const Icon = getFileIcon(file.file_type);
                  return (
                    <div 
                      key={file.id} 
                      className="flex flex-col md:flex-row md:items-center justify-between p-3 md:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-3"
                      data-testid={`document-${file.id}`}
                    >
                      <div className="flex items-center gap-3 md:gap-4">
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-[#2E2A88]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 md:w-6 md:h-6 text-[#2E2A88]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-manrope font-medium text-sm md:text-base truncate">{file.original_name}</p>
                          <p className="font-manrope text-xs md:text-sm text-gray-500">
                            {formatFileSize(file.size)} · {format(new Date(file.uploaded_at), 'dd.MM.yyyy')}
                          </p>
                        </div>
                      </div>
                      {file.file_exists === false ? (
                        <span className="text-xs text-red-600 font-medium w-full md:w-auto text-center md:text-right">Datei fehlt auf Server</span>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDownload(file)}
                          className="w-full md:w-auto"
                          data-testid={`download-btn-${file.id}`}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Herunterladen
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 md:py-12">
                <FileText className="w-12 h-12 md:w-16 md:h-16 text-gray-300 mx-auto mb-4" />
                <p className="font-manrope text-gray-500 mb-2 text-sm md:text-base">Keine Dokumente vorhanden</p>
                <p className="font-manrope text-xs md:text-sm text-gray-400">
                  Hier werden Ihre Rechnungen, Angebote und andere Dokumente angezeigt.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
